'use client'

import { useMemo, useState } from 'react'
import { ReceiptText } from 'lucide-react'
import OrderCard from './OrderCard'

type FilterKey = 'all' | 'active' | 'delivered' | 'canceled'

const FILTERS: { key: FilterKey, label: string }[] = [
  { key: 'all', label: 'Бүгд' },
  { key: 'active', label: 'Хүргэгдэж буй' },
  { key: 'delivered', label: 'Хүргэгдсэн' },
  { key: 'canceled', label: 'Цуцалсан' },
]

const ACTIVE_STATUSES = new Set(['pending', 'paid', 'ready', 'shipped'])
const DELIVERED_STATUSES = new Set(['delivered'])
const CANCELED_STATUSES = new Set(['canceled', 'refunded', 'partiallyRefunded', 'deliveryFailed'])

export default function OrderList({ orderList }: { orderList: any }) {
  const orders: any[] = orderList || []
  const isLoading = orderList === undefined
  const [filter, setFilter] = useState<FilterKey>('all')

  const filtered = useMemo(() => {
    if (filter === 'all') return orders
    return orders.filter(o => {
      if (filter === 'active') return ACTIVE_STATUSES.has(o.status)
      if (filter === 'delivered') return DELIVERED_STATUSES.has(o.status)
      if (filter === 'canceled') return CANCELED_STATUSES.has(o.status)
      return true
    })
  }, [orders, filter])

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-16">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>
          Сүүлийн захиалгууд{orders.length > 0 ? ` · ${orders.length}` : ''}
        </div>
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
          {FILTERS.map(f => {
            const active = f.key === filter
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className="h-8 px-3.5 rounded-full text-[12px] font-medium transition-colors shrink-0"
                style={{
                  background: active ? 'rgba(255,255,255,0.07)' : 'transparent',
                  color: active ? 'white' : 'rgba(255,255,255,0.55)',
                }}
              >
                {f.label}
              </button>
            )
          })}
        </div>
      </div>

      {isLoading ? (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="py-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="h-3 w-32 rounded animate-pulse mb-2" style={{ background: 'rgba(255,255,255,0.04)' }} />
              <div className="h-4 w-48 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
            </div>
          ))}
        </div>
      ) : filtered.length > 0 ? (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          {filtered.map(order => <OrderCard key={order.id} order={order} />)}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 py-24" style={{ color: 'rgba(255,255,255,0.5)' }}>
          <ReceiptText className="h-8 w-8" />
          <div className="text-[14px]">
            {filter === 'all' ? 'Захиалга байхгүй байна' : 'Энэ ангиллын захиалга алга'}
          </div>
        </div>
      )}
    </div>
  )
}
